import { Palette } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { storeCustomizePath } from '@/shared/config';
import { cn } from '@/shared/ui';

export interface GuestDesignSummaryProps {
  readonly productId: string;
  readonly productName: string;
  readonly colorName: string;
  /**
   * 에디터가 [견적 요청하기] 때 만든 앞면 미리보기 그림(이미지 data URL) — 있으면 상품·색상 요약 위에 보여 준다.
   * 없으면(다른 기기·보관 실패 등) 그림 대신 자리표시 타일(아이콘)과 글 요약을 보여 준다. 읽는 곳은 위젯의 임시 작업 훅이다
   * (같은 계층 feature 끼리는 import 하지 않는다).
   */
  readonly previewSrc?: string | null | undefined;
  /** 놓일 자리(그리드 칸) — 폼이 정한다. */
  readonly className?: string;
}

/**
 * 비회원 요청 화면의 디자인 정보 — 비회원 디자인은 요청 순간까지 서버에 없어 서버 썸네일이 없다(설계 G5).
 * 에디터가 남긴 앞면 미리보기 그림이 있으면 그것을 위에 보여 주고(설계 G12), 없으면 같은 자리에 자리표시 타일(팔레트 아이콘)을 두고 옆에 글 요약을 보여 준다.
 * 상품·색상, "요청하면 디자인이 고정된다" 안내, 에디터로 돌아가 고치는 링크가 함께 나온다(에디터는 임시 작업을 되살린다).
 */
export const GuestDesignSummary = ({
  productId,
  productName,
  colorName,
  previewSrc,
  className,
}: GuestDesignSummaryProps): ReactElement => (
  <section
    aria-label="디자인 정보"
    className={cn(
      'flex gap-5 border-b border-foreground/10 pb-6 lg:border-b-0 lg:pb-0',
      previewSrc ? 'flex-col' : 'items-center',
      className,
    )}
  >
    {previewSrc ? (
      <div className="flex max-h-72 w-full items-center justify-center bg-muted/70 p-4 sm:max-h-80">
        <img
          src={previewSrc}
          alt="요청할 디자인 앞면"
          className="max-h-60 w-auto max-w-full object-contain sm:max-h-72"
        />
      </div>
    ) : (
      <div
        aria-hidden
        className="flex size-28 shrink-0 items-center justify-center bg-muted/70 text-muted-foreground sm:size-32 xl:size-40"
      >
        <Palette strokeWidth={1.25} className="size-8" />
      </div>
    )}
    <div className="flex min-w-0 flex-col gap-2">
      <p className="text-xs text-muted-foreground">상품 · 색상</p>
      <p className="text-lg leading-snug font-semibold tracking-[-0.01em] wrap-anywhere">{`${productName} · ${colorName}`}</p>
      <p className="text-xs leading-relaxed text-muted-foreground">
        에디터에서 만든 디자인 그대로 요청해요. 요청한 뒤에는 디자인이 고정돼요.
      </p>
      <Link
        to={storeCustomizePath(productId)}
        className="w-fit text-sm underline underline-offset-4"
      >
        디자인 수정하기
      </Link>
    </div>
  </section>
);
